import { sortBy } from 'lodash-es'

import type { WorldLevel } from '@/queries/constants'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import {
  getLevelRequirements,
  getNextLevel,
  LevelRequirementStatus,
} from '@/client/services/worlds'
import { LEVEL_REQUIREMENT_TO_QUERY_FUNCTION } from '@/client/services/worlds/progression/queries'

import type { GrowthPathLevelStepDetails } from '@/components/Worlds/Journey/types'

import { worldDetailsFromQuery } from '../../transforms/detailsFromQueries'
import type { WorldDetails } from '../../types'

export default function useMemberWorldsWithNextStep<K extends (keyof WorldDetails)[]>(
  fields: [...K],
  params: {
    worldSlugsToInclude?: string[]
  } = {}
): (Pick<WorldDetails, K[number]> & {
  nextLevelSteps: GrowthPathLevelStepDetails[]
  currentLevel: WorldLevel
})[] {
  const user = useUser()
  const query = useQuery()

  const worldInfos = Object.values(user.worlds)
    .filter(
      world =>
        params?.worldSlugsToInclude == null || params.worldSlugsToInclude.includes(world.slug)
    )
    .map(({ slug, level }) => {
      const world = query.one(WorldQ.where({ slug }))
      const worldPosition = world(WorldQ.position) ?? 0
      const worldDetails = worldDetailsFromQuery(fields, {
        world,
        userId: user.id,
      })
      const nextLevel = getNextLevel({ currentLevel: level, worldSlug: slug })

      if (nextLevel == null) {
        return {
          ...worldDetails,
          nextLevelSteps: [],
          position: worldPosition,
          currentLevel: level,
        }
      }

      const levelRequirements = getLevelRequirements({ worldSlug: slug })[nextLevel]

      const steps = levelRequirements
        .map(requirement => {
          const requirementQuery = LEVEL_REQUIREMENT_TO_QUERY_FUNCTION[requirement.type]({
            userId: user.id,
            worldSlug: slug,
            requirement,
          })
          const status = query(requirementQuery) ?? LevelRequirementStatus.Incomplete
          return {
            requirement,
            status,
            level: nextLevel,
          }
        })
        .filter(item => item.status !== LevelRequirementStatus.NotApplicable)

      return {
        ...worldDetails,
        nextLevelSteps: steps,
        position: worldPosition,
        currentLevel: level,
      }
    })

  return sortBy(worldInfos, world => world.position)
}
